/**
 * TaskExecutionContextPanel — Contexto rápido (referência lateral).
 */
import React from 'react';
import { Clock, User } from 'lucide-react';
import { EnterpriseQueueSkeleton } from './EnterpriseQueueSkeleton';
import type { PendencyItem } from '../utils/pendenciesUtils';

interface TaskExecutionContextPanelProps {
  responsible: string;
  slaLabel: string;
  slaTone?: 'default' | 'warning' | 'danger' | 'ok';
  openedAtLabel?: string;
  pendencyItems?: PendencyItem[];
  slaAlert?: string | null;
  isLoading?: boolean;
  onOpenDetails?: () => void;
}

const SLA_COLOR: Record<NonNullable<TaskExecutionContextPanelProps['slaTone']>, string> = {
  default: 'text-slate-800 dark:text-slate-100',
  ok: 'text-emerald-700 dark:text-emerald-400',
  warning: 'text-amber-700 dark:text-amber-400',
  danger: 'text-red-700 dark:text-red-400',
};

export const TaskExecutionContextPanel: React.FC<TaskExecutionContextPanelProps> = ({
  responsible,
  slaLabel,
  slaTone = 'default',
  openedAtLabel,
  pendencyItems = [],
  slaAlert,
  isLoading,
  onOpenDetails,
}) => (
  <aside className="flex h-full min-h-0 w-[188px] min-w-[176px] shrink-0 flex-col self-stretch overflow-hidden border-l border-slate-200/80 bg-white dark:border-[var(--border-default)] dark:bg-[var(--bg-elevated)]">
    <header className="flex shrink-0 items-center justify-between border-b border-slate-100 px-2.5 py-2 dark:border-slate-800">
      <p className="text-[8px] font-bold uppercase tracking-[0.12em] text-slate-400">Referência</p>
      {onOpenDetails && (
        <button type="button" onClick={onOpenDetails} className="text-[8px] font-semibold text-blue-600 hover:underline dark:text-blue-400">
          Detalhes
        </button>
      )}
    </header>

    {isLoading ? (
      <EnterpriseQueueSkeleton rows={4} />
    ) : (
      <div className="min-h-0 flex-1 overflow-y-auto px-2.5 py-2">
        {slaAlert && (
          <div className="mb-2 flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 dark:bg-amber-950/20">
            <Clock size={10} className="mt-0.5 shrink-0 text-amber-600" />
            <p className="text-[8px] leading-snug text-amber-800 dark:text-amber-200">{slaAlert}</p>
          </div>
        )}

        <dl className="space-y-2.5">
          <div>
            <dt className="flex items-center gap-1 text-[7px] font-bold uppercase tracking-wider text-slate-400">
              <User size={9} /> Responsável
            </dt>
            <dd className="mt-0.5 truncate text-[11px] font-semibold text-slate-800 dark:text-slate-100">{responsible}</dd>
          </div>
          <div>
            <dt className="text-[7px] font-bold uppercase tracking-wider text-slate-400">SLA / Prazo</dt>
            <dd className={`mt-0.5 text-[11px] font-bold tabular-nums ${SLA_COLOR[slaTone]}`}>{slaLabel}</dd>
          </div>
          {openedAtLabel && (
            <div>
              <dt className="text-[7px] font-bold uppercase tracking-wider text-slate-400">Abertura</dt>
              <dd className="mt-0.5 text-[10px] text-slate-600 dark:text-slate-300">{openedAtLabel}</dd>
            </div>
          )}
        </dl>

        {pendencyItems.length > 0 && (
          <div className="mt-3 border-t border-slate-100 pt-2 dark:border-slate-800">
            <p className="mb-1.5 text-[7px] font-bold uppercase tracking-wider text-amber-600">
              {pendencyItems.length} pendência{pendencyItems.length !== 1 ? 's' : ''}
            </p>
            <ul className="space-y-1">
              {pendencyItems.slice(0, 5).map((item) => (
                <li key={item.id} className="truncate text-[9px] text-slate-600 dark:text-slate-300">
                  · {item.label}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )}
  </aside>
);

export default TaskExecutionContextPanel;
